import { createContext, useCallback, useContext, useMemo, useState } from "react";

/* Admin i18n — canonical strings are English; NO maps to Norwegian.
   Missing keys fall back to English silently. */
const NO = {
  // Chrome
  "Website": "Nettside",
  "View site": "Se nettsiden",
  "Sign out": "Logg ut",
  "Signing out…": "Logger ut…",
  "Search": "Søk",
  "Switch to light theme": "Bytt til lyst tema",
  "Switch to dark theme": "Bytt til mørkt tema",
  "Switch language": "Bytt språk",
  "Overview": "Oversikt",
  "Partners": "Partnere",
  "Announcements": "Kunngjøringer",
  "Signups": "Påmeldinger",
  "Settings": "Innstillinger",
  "Budget": "Budsjett",
  "Revenue": "Inntekter",
  "Costs": "Kostnader",
  "Scenarios": "Scenarioer",
  "Scenario comparison": "Scenario-sammenligning",
  "Next step": "Neste steg",
  "Due date": "Frist",
  "Activity": "Aktivitet",
  "Log a note (added to the timeline)": "Loggfør notat (legges i tidslinjen)",
  "Roadmap": "Veikart",
  "Adjust per scenario": "Juster per scenario",
  "Revenue engines": "Inntektsmotorer",
  "Break-even & variances": "Break-even & avvik",
  "Lowest": "Lavest",
  "Event": "Event",
  "of costs": "av kostnadene",
  "Key risks": "Nøkkelrisikoer",
  "Target": "Mål",
  "target": "mål",
  "signed of": "signert av",
  "copied": "kopiert",
  "Commercial target architecture": "Kommersiell målarkitektur",
  "Partner revenue": "Partnerinntekter",
  "Partner revenue assumption (NOK, blank = pipeline)": "Partnerinntekt-antakelse (NOK, tom = pipeline)",
  "* = planning assumption above real pipeline — reality wins once signed value exceeds it.": "* = plan-antakelse over reell pipeline — virkeligheten vinner når signert verdi passerer den.",
  "Target ≠ forecast — forecast is calculated from the real pipeline below. Scenario assumptions live under Scenarios.": "Mål ≠ forecast — forecast beregnes fra den reelle pipelinen under. Scenario-antakelser ligger under Scenarioer.",
  "Share": "Del",
  "Share external link with password": "Del ekstern lenke med passord",
  "Download as PDF": "Last ned som PDF",
  "Generating …": "Genererer …",
  "PDF downloaded": "PDF lastet ned",
  "Meetings": "Møter",
  "Share external link": "Del ekstern lenke",
  "Password-protected memo": "Passordbeskyttet memo",
  "PDF — dark": "PDF — mørk",
  "PDF — light": "PDF — lys",
  "Presentation style": "Presentasjonsstil",
  "Print-friendly · smaller file": "Utskriftsvennlig · mindre fil",
  "External link": "Ekstern lenke",
  "Public, password-protected budget memo (read-only, no sponsor names). Share the link and password separately.": "Offentlig, passordbeskyttet budsjett-memo (kun lesing, uten sponsornavn). Del lenken og passordet hver for seg.",
  "Link": "Lenke",
  "Password": "Passord",
  "Active": "Aktiv",
  "Deactivated": "Deaktivert",
  "Activate": "Aktiver",
  "Deactivate": "Deaktiver",
  "Link activated": "Lenken er aktivert",
  "Link deactivated": "Lenken er deaktivert",
  "The link is stable — deactivate to close access immediately.": "Lenken er stabil — deaktiver for å stenge tilgang umiddelbart.",
  "Loading …": "Laster …",

  // Control Center
  "Loading Control Center …": "Laster Control Center …",
  "capacity": "kapasitet",
  "date not set": "dato ikke satt",
  "Set event date": "Sett eventdato",
  "Event date (YYYY-MM-DD):": "Eventdato (ÅÅÅÅ-MM-DD):",
  "Event date set": "Eventdato satt",
  "Invalid date": "Ugyldig dato",
  "Could not load the overview": "Kunne ikke laste oversikten",
  "days to show": "dager til show",
  "Budget · base forecast": "Budsjett · base forecast",
  "margin": "margin",
  "revenue": "inntekter",
  "costs": "kostnader",
  "vs budget": "vs budget",
  "funding": "funding",
  "Budget not set up yet": "Budsjett ikke satt opp ennå",
  "confirmed": "bekreftet",
  "active": "aktive",
  "awaiting": "venter",
  "blockers": "blockers",
  "sessions": "sessions",
  "published": "publisert",
  "signed": "signert",
  "weighted pipeline": "vektet pipeline",
  "on the website": "på nettsiden",
  "Early Access signups": "Early Access-påmeldte",
  "overdue": "over frist",
  "due next 7 days": "med frist neste 7 dager",
  "open": "åpne",
  "Experience": "Experience",
  "of": "av",
  "Needs attention": "Needs attention",
  "Nothing urgent. Well done.": "Ingenting som haster. Godt jobbet.",
  "Next 7 days": "Neste 7 dager",
  "No deadlines in the next 7 days.": "Ingen frister de neste 7 dagene.",
  "d overdue": "d over frist",
  "Latest activity": "Siste aktivitet",
  "No activity yet.": "Ingen aktivitet ennå.",
  "now": "nå",

  // Budget — shared
  "Loading budget …": "Laster budsjett …",
  "Something went wrong": "Noe gikk galt",
  "Executive summary — forecast, assumptions, break-even": "Executive-oversikt — forecast, forutsetninger, break-even",
  "Four engines — physical, digital, dinner, partners": "Fire motorer — fysisk, digital, dinner, partnere",
  "Driver-based cost lines · quote → contract → invoice": "Driver-baserte kostnadslinjer · quote → kontrakt → faktura",
  "Monthly liquidity, payments incl. VAT": "Måned-for-måned likviditet, betalinger inkl. mva.",
  "Downside · Base · Upside": "Downside · Base · Upside",
  "NOK ex VAT · cash flow incl. VAT": "NOK eks. mva. · cash flow inkl. mva.",
  "physical": "fysisk",
  "digital": "digital",
  "partners": "partnere",
  "production": "produksjon",
  "dinner guests": "middagsgjester",
  "phys": "fys",
  "dig": "dig",
  "dinner": "middag",
  "Ticket product created": "Billettprodukt opprettet",
  "Sponsor created": "Sponsor opprettet",
  "Line created": "Linje opprettet",
  "Deleted": "Slettet",

  // Budget label maps
  "Venue": "Venue",
  "Production": "Produksjon",
  "Speakers": "Speakers",
  "Food & Beverage": "Mat & drikke",
  "Marketing": "Marketing",
  "Technology": "Teknologi",
  "Staffing": "Bemanning",
  "Partner / Expo": "Partner / Expo",
  "Other": "Annet",
  "Other revenue": "Andre inntekter",
  "Lead": "Lead",
  "Dialog": "Dialog",
  "Proposal": "Tilbud",
  "Verbal yes": "Muntlig ja",
  "Signed": "Signert",
  "Fixed": "Fast",
  "Per physical attendee": "Per fysisk deltaker",
  "Per digital attendee": "Per digital deltaker",
  "Per dinner guest": "Per middagsgjest",
  "Tiered": "Trinnvis",
  "% of revenue": "% av inntekt",
  "Planned": "Planlagt",
  "Quote received": "Tilbud mottatt",
  "Contracted": "Kontraktfestet",
  "Invoiced": "Fakturert",
  "Paid": "Betalt",
  "Signed only": "Kun signerte",
  "Weighted pipeline": "Vektet pipeline",
  "Optimistic (≥ proposal in full)": "Optimistisk (≥ tilbud fullt ut)",
  "Physical": "Fysisk",
  "Digital": "Digital",
  "Dinner": "Dinner",

  // Budget — Overview
  "Forecast profit": "Forecast resultat",
  "actuals to date": "actuals hittil",
  "Forecast revenue": "Forecast inntekter",
  "Forecast costs": "Forecast kostnader",
  "Physical sales": "Fysisk salg",
  "sell-through": "sell-through",
  "in the room": "i salen",
  "Digital passes": "Digitale pass",
  "net revenue": "netto inntekt",
  "Sponsorship": "Sponsorater",
  "Lowest cash": "Laveste cash",
  "funding required": "funding nødvendig",
  "Assumptions": "Forutsetninger",
  "Edit assumptions": "Rediger forutsetninger",
  "Physical inventory · Peer Gynt": "Fysisk inventory · Peer Gynt",
  "venue": "venue",
  "reserve": "reserve",
  "comps": "comps",
  "sellable": "selgbare",
  "allocated to products": "allokert til produkter",
  "unallocated": "uallokert",
  "Dinner inventory": "Dinner-inventory",
  "Executive": "Executive",
  "partner/speaker": "partner/speaker",
  "sellable add-ons": "selgbare add-ons",
  "allocated": "allokert",
  "guests in": "gjester i",
  "Scenario drivers": "Scenario-drivere",
  "Physical sell-through": "Fysisk sell-through",
  "Digital passes sold": "Digitale pass solgt",
  "Sponsor revenue": "Sponsorinntekt",
  "Production level": "Produksjonsnivå",
  "Dinner guests": "Middagsgjester",
  "Physical summit": "Fysisk summit",
  "tickets": "billetter",
  "passes": "pass",
  "add-ons": "add-ons",
  "Summit dinner": "Summit-middag",
  "Break-even": "Break-even",
  "physical tickets required": "fysiske billetter kreves",
  "sold in this forecast": "solgt i denne forecasten",
  "revenue short of break-even": "inntekt igjen til break-even",
  "Break-even reached at current forecast": "Break-even nådd med gjeldende forecast",
  "Assumes": "Forutsetter",
  "sponsors": "sponsorer",
  "other": "annet",
  "contribution": "bidrag",
  "per ticket": "per billett",
  "Costs by category": "Kostnader per kategori",
  "Contingency": "Contingency",
  "Largest variances (Base)": "Største avvik (Base)",
  "No variances yet.": "Ingen avvik ennå.",

  // Budget — Revenue
  "net": "netto",
  "Add product": "Nytt produkt",
  "Add sponsor": "Ny sponsor",
  "Add line": "Ny linje",
  "soft pool, no venue constraint": "myk pool, ingen venue-begrensning",
  "passes sold": "pass solgt",
  "add-ons sold": "add-ons solgt",
  "guests total": "gjester totalt",
  "No physical products.": "Ingen fysiske produkter.",
  "No digital products.": "Ingen digitale produkter.",
  "No dinner add-ons.": "Ingen dinner add-ons.",
  "No sponsors in pipeline.": "Ingen sponsorer i pipeline.",
  "No other revenue lines.": "Ingen andre inntektslinjer.",
  "Product": "Produkt",
  "Price ex VAT": "Pris eks. mva.",
  "Capacity": "Kapasitet",
  "Sell-through": "Sell-through",
  "Sold": "Solgt",
  "Net revenue": "Netto inntekt",
  "Sponsor": "Sponsor",
  "Package": "Pakke",
  "Value": "Verdi",
  "Stage": "Fase",
  "Probability": "Sannsynlighet",
  "Weighted": "Vektet",
  "Committed": "Committed",
  "Total pipeline": "Total pipeline",
  "Used in": "Brukt i",
  "Scenario uses": "Scenarioet bruker",
  "Agreements are shared with the Partners module.": "Avtalene deles med Partners-modulen.",
  "Grants, side events etc.": "Tilskudd, sidearrangementer osv.",
  "Name": "Navn",
  "Forecast": "Forecast",
  "Actual": "Actual",

  // Budget — Costs
  "incl.": "inkl.",
  "contingency": "contingency",
  "line": "linje",
  "lines": "linjer",
  "Line": "Linje",
  "Driver": "Driver",
  "Status": "Status",
  "Quote": "Quote",
  "Subtotal": "Subtotal",
  "Overridden in this scenario": "Overstyrt i dette scenarioet",
  "/head": "/hode",

  // Budget — Cash Flow
  "Lowest projected cash": "Laveste projiserte cash",
  "Funding required": "Funding nødvendig",
  "to stay cash-positive through": "for å holde cash positiv gjennom",
  "No external funding required in": "Ingen ekstern funding nødvendig i",
  "Closing cash": "Utgående cash",
  "After": "Etter",
  "opening": "inngående",
  "Cash balance": "Cash-balanse",
  "payments incl. VAT": "betalinger inkl. mva.",
  "Month": "Måned",
  "In": "Inn",
  "Out": "Ut",
  "Net": "Netto",
  "Closing": "Utgående",
  "Hide settings": "Skjul innstillinger",
  "Settings — period, opening cash & sales curve": "Innstillinger — periode, inngående cash & salgskurve",
  "Opening cash": "Inngående cash",
  "From month": "Fra måned",
  "To month": "Til måned",
  "Event month": "Eventmåned",
  "Ticket sales curve (share of sales per month)": "Billettsalgskurve (andel av salget per måned)",
  "Save settings": "Lagre innstillinger",
  "Transactions": "Transaksjoner",
  "Money in": "Penger inn",
  "Money out": "Penger ut",
  "No payments this month.": "Ingen betalinger denne måneden.",
  "Net this month": "Netto denne måneden",
  "Closing balance": "Utgående saldo",
  "All amounts incl. VAT": "Alle beløp inkl. mva.",
  "Ticket sales": "Billettsalg",
  "Contingency reserve": "Contingency-reserve",
  "scenario": "scenario",

  // Budget — Scenarios
  "Physical sold": "Fysisk solgt",
  "In the room": "I salen",
  "Profit": "Resultat",
  "Margin": "Margin",
  "Sponsor revenue counted as": "Sponsorinntekt telles som",
  "Attendance is driven by per-product sell-through (Revenue). Comps come from the inventory model. Individual lines can be overridden per scenario in the line editor.":
    "Oppmøte styres av sell-through per produkt (Inntekter). Comps kommer fra inventory-modellen. Enkeltlinjer kan overstyres per scenario i linje-editoren.",
  "Percent": "Prosent",
  "Save": "Lagre",
  "Calculated on these categories": "Beregnes på disse kategoriene",
  "Sponsor stages — default probability": "Sponsorfaser — standard sannsynlighet",
  "Save probabilities": "Lagre sannsynligheter",
  "Can be overridden per sponsor in the sponsor editor.": "Kan overstyres per sponsor i sponsor-editoren.",

  // Budget — modals/drawers
  "New ticket product": "Nytt billettprodukt",
  "Edit ticket product": "Rediger billettprodukt",
  "Channel": "Kanal",
  "Fees % (ticketing/payment)": "Gebyrer % (billettering/betaling)",
  "VAT %": "Mva. %",
  "Sell-through per scenario (%)": "Sell-through per scenario (%)",
  "Cancel": "Avbryt",
  "Saving …": "Lagrer …",
  "Physical inventory": "Fysisk inventory",
  "Dinner inventory:": "Dinner-inventory:",
  "Digital is a soft pool — no venue constraint.": "Digital er en myk pool — ingen venue-begrensning.",
  "Edit sponsor": "Rediger sponsor",
  "New sponsor": "Ny sponsor",
  "Probability (override)": "Sannsynlighet (override)",
  "Default:": "Standard:",
  "Owner": "Ansvarlig",
  "None": "Ingen",
  "Notes": "Notater",
  "Expected payment (empty = event month)": "Forventet betaling (tom = eventmåned)",
  "Change note (logged)": "Endringsnotat (logges)",
  "Change log": "Endringslogg",
  "Edit line": "Rediger linje",
  "New cost line": "Ny kostnadslinje",
  "New revenue line": "Ny inntektslinje",
  "Supplier": "Leverandør",
  "Category": "Kategori",
  "Quote amount (ex VAT)": "Tilbudsbeløp (eks. mva.)",
  "Status is a document layer (quote → contract → invoice) — it never changes the numbers by itself.":
    "Status er et dokumentlag (quote → kontrakt → faktura) — det endrer aldri tallene i seg selv.",
  "Cost driver": "Kostnadsdriver",
  "Type": "Type",
  "Cost per physical attendee": "Kostnad per fysisk deltaker",
  "Cost per digital attendee": "Kostnad per digital deltaker",
  "Cost per dinner guest": "Kostnad per middagsgjest",
  "Cost per tier": "Kostnad per trinn",
  "% of total revenue": "% av total inntekt",
  "Unit cost": "Enhetskostnad",
  "Per # attendees": "Per # deltakere",
  "Forecast is computed automatically from each scenario’s drivers.": "Forecast beregnes automatisk fra driverne i hvert scenario.",
  "Numbers (NOK ex VAT)": "Tall (NOK eks. mva.)",
  "Forecast build-up (optional — forecast = sum of components)": "Forecast-oppbygging (valgfritt — forecast = sum av komponenter)",
  "Add component": "Legg til komponent",
  "Override forecast per scenario (optional)": "Overstyr forecast per scenario (valgfritt)",
  "Auto": "Auto",
  "Payment plan (empty = event month · cash flow uses incl. VAT)": "Betalingsplan (tom = eventmåned · cash flow bruker inkl. mva.)",
  "Change note (logged in the change log)": "Endringsnotat (logges i endringsloggen)",
  "Save line": "Lagre linje",
  "Delete": "Slett",
  "Delete this line?": "Slette denne linjen?",
  "Venue capacity": "Venue-kapasitet",
  "Operational reserve": "Operasjonell reserve",
  "Comps (partner / speaker / press / strategic)": "Comps (partner / speaker / presse / strategisk)",
  "Add comp category": "Legg til comp-kategori",
  "Incl. in Executive": "Inkl. i Executive",
  "Partner/speaker": "Partner/speaker",
  "Save assumptions": "Lagre forutsetninger",
  "Sellable inventory is derived automatically. Ticket products allocate against it — the Overview warns if products are over-allocated.":
    "Selgbart inventory beregnes automatisk. Billettprodukter allokerer mot det — Oversikten varsler hvis produkter er over-allokert.",
  "Add month": "Legg til måned",
  "Sum": "Sum",
};

const LangCtx = createContext({ lang: "no", setLang: () => {}, t: (s) => s });

export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    const v = localStorage.getItem("nas_admin_lang");
    return v === "en" ? "en" : "no";
  });
  const setLang = useCallback((l) => {
    localStorage.setItem("nas_admin_lang", l);
    setLangState(l);
  }, []);
  const t = useCallback((s) => (lang === "no" ? (NO[s] ?? s) : s), [lang]);
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);
